import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { promptsService } from '../../services/prompts.service';
import type { PromptListParams } from '../../services/prompts.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PromptWithId } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import { StatusChip } from '../../components/common/panel';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { formatDate } from 'sharedUi/dateUtils';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Name', filter: true },
  { field: 'type', header: 'Type', filter: true },
  { field: 'aiRole', header: 'AI Role', filter: true },
  { field: 'llm', header: 'Model', filter: true },
  { field: 'tech', header: 'Technology', filter: true },
];

const PromptList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'prompt',
    basePath: '/prompt-table',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.PROMPT_MASTER) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.PROMPT_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.PROMPT_MASTER);

  const [prompts, setPrompts] = useState<PromptWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showGlobal, setShowGlobal] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states for server-side sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  const fetchPrompts = useCallback(
    async (
      page: number = 1,
      size: number = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);

        const params: PromptListParams = {
          page,
          page_size: size,
          include_deleted: includeDeleted,
        };

        if (debouncedSearchText && debouncedSearchText.trim()) {
          params.search = debouncedSearchText.trim();
        }
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showGlobal;

        const response = await promptsService.list(params);
        const mappedData = (response.data || []).map((prompt) => ({ ...prompt, id: prompt._id }));
        setPrompts(mappedData);

        // Handle both total and totalItems from API response
        const total = (response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0;
        setTotalRecords(total);
      } catch (error) {
        console.error('Failed to fetch prompts:', error);
        showError(extractErrorMessage(error, 'Failed to load prompts'));
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, debouncedSearchText, selectedProjectId, showGlobal, pageSize, sortField, sortOrder, filters, showError]
  );

  // Handle page change from DataTable
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchPrompts(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort change from DataTable
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchPrompts(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchPrompts(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setFirst(0);
    fetchPrompts(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedPromptId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPromptId) return;

    try {
      setDeleteLoading(true);
      await promptsService.softDelete(selectedPromptId);
      setDeleteDialogOpen(false);
      setSelectedPromptId(null);
      fetchPrompts(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      console.error('Failed to delete prompt:', error);
      setDeleteDialogOpen(false);
      setSelectedPromptId(null);
      showError(extractErrorMessage(error, 'Failed to delete prompt'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedPromptId(null);
  };

  const columns: SaasColumn<PromptWithId>[] = useMemo(() => {
    const cols: SaasColumn<PromptWithId>[] = [
      { field: 'name', header: 'Name', sortable: true, filter: true, hideable: false },
      { field: 'type', header: 'Type', sortable: true, filter: true },
      {
        // Which version runs, and whether someone is drafting the next one.
        field: 'version',
        header: 'Version',
        body: (r) => (
          <span className="flex flex-wrap items-center gap-1" data-testid="PromptList.Version">
            <StatusChip tone="success" dot={false}>v{r.version ?? 1} · Active</StatusChip>
            {r.draftVersion != null && <StatusChip tone="warn" dot={false}>Draft v{r.draftVersion}</StatusChip>}
          </span>
        ),
      },
      { field: 'aiRole', header: 'AI Role', sortable: true, filter: true },
      { field: 'llm', header: 'Model', sortable: true, filter: true, body: (r) => r.llm || '-' },
      { field: 'tech', header: 'Technology', sortable: true, filter: true, body: (r) => r.tech || '-' },
      { field: 'createdAt', header: 'Created At', sortable: true, body: (r) => formatDate(r.createdAt) },
    ];

    if (includeDeleted) {
      cols.push({
        field: 'status',
        header: 'Status',
        body: (r) => {
          const deleted = (r as any).isDelete || r.isDeleted;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (r) => {
        const deleted = (r as any).isDelete || r.isDeleted;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(r._id)}
              data-testid="PromptList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
            </button>
            {!deleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(r._id)}
                    data-testid="PromptList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(r._id)}
                    data-testid="PromptList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, canManageProjects, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.PROMPT_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-Prompt-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-comments"
          title="Prompt Management"
          subtitle="Manage AI prompts, templates, and configurations"
          testId="Prompt.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="prompt"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={prompts}
            loading={loading}
            dataKey="_id"
            emptyMessage="No prompts found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search prompts..."
            testId="PromptList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="PromptList.Filter.Project"
                  />
                )}
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  <InputSwitch
                    checked={showGlobal}
                    onChange={(e: any) => setShowGlobal(!!e.value)}
                    data-testid="PromptList.Toggle.ShowGlobal"
                  />
                  Show core
                </label>
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchPrompts(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                  testId="PromptList.Button.Refresh"
                />
                {canCreate && (
                  <Button
                    label="Create Prompt"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="PromptList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this prompt? This will mark it as deleted."
          testId="PromptList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default PromptList;
